import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
} from '@mui/material';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  SimplifiedWorkflowStepFormData,
  StepType,
} from '../../../../data/models/simplified-workflow.model';
import TriggerStepForm from './TriggerStepForm';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import * as uiSlice from '../../../../data/ui.slice';
import ActionStepForm from './ActionStepForm';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmationDialog from '../../../ConfirmationDialog';

export interface SimplifiedWorkflowStepRef {
  save: () => boolean;
  resetForm: () => void;
  getFormData: () => SimplifiedWorkflowStepFormData;
}

interface SimplifiedWorkflowStepProps {
  stepData: SimplifiedWorkflowStepFormData;
  handleFormUpdate: () => void;
  handleDeleteStep: (stepId: string) => void;
}

const PREFIX = 'SimplifiedWorkflowStep';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  divider: `${PREFIX}-divider`,
  formContainer: `${PREFIX}-formContainer`,
  stepNameContainer: `${PREFIX}-stepNameContainer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  [`& .${classes.header}`]: {
    margin: 0,
  },
  [`& .${classes.divider}`]: {
    marginBottom: theme.spacing(4),
  },
  [`& .${classes.formContainer}`]: {
    gap: theme.spacing(3),
  },
  [`& .${classes.stepNameContainer}`]: {
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const SimplifiedWorkflowStep = forwardRef(
  (
    {
      stepData,
      handleFormUpdate,
      handleDeleteStep,
    }: SimplifiedWorkflowStepProps,
    ref,
  ) => {
    const { t } = useTranslation('common');
    const dispatch = useAppDispatch();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
      useState(false);

    useImperativeHandle(ref, () => ({ save, resetForm, getFormData }));

    const methods = useForm<SimplifiedWorkflowStepFormData>();

    const { control, getValues, reset, trigger } = methods;

    const { isValid } = useFormState({ control });

    useEffect(() => {
      const defaultValues = {
        ...stepData,
      };
      reset(defaultValues); // load default values
    }, [stepData]);

    const save = () => {
      if (isValid) {
        return true;
      } else {
        trigger();
        dispatch(uiSlice.setErrorMessage('snackbar.workflowStepSaveFailed'));
        return false;
      }
    };

    const resetForm = () => {
      reset({ tempId: '' });
    };

    const getFormData = () => {
      return getValues();
    };

    const title = useWatch({ control, name: 'title' }) ?? stepData.title;

    const handleDeleteStepClick = () => {
      setIsMenuOpen(false);
      setIsConfirmationDialogOpen(true);
    };

    const handleDeleteStepConfirm = () => {
      setIsConfirmationDialogOpen(false);
      handleDeleteStep(stepData.tempId ?? '');
    };

    const handleMoreOptionsClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      setIsMenuOpen(true);
    };

    const handleMenuClose = () => {
      setIsMenuOpen(false);
    };

    return (
      <StyledBox>
        <FormProvider {...methods}>
          <Box className={classes.container}>
            <Stack direction="row" className={classes.stepNameContainer}>
              <h2 className={classes.header}>{title}</h2>
              {stepData?.type !== StepType.Trigger && (
                <IconButton
                  ref={menuButtonRef}
                  onClick={handleMoreOptionsClick}
                  size="large">
                  <MoreVertIcon />
                </IconButton>
              )}
            </Stack>
            <Divider className={classes.divider} />
            <Stack direction="column" className={classes.formContainer}>
              {stepData?.type === StepType.Trigger && (
                <TriggerStepForm handleFormUpdate={handleFormUpdate} />
              )}
              {stepData?.type === StepType.Action && (
                <ActionStepForm handleFormUpdate={handleFormUpdate} />
              )}
            </Stack>
          </Box>
        </FormProvider>
        <ConfirmationDialog
          title={t('workflows.confirmDeleteStepTitle')}
          message={t('workflows.confirmDeleteStepMessage')}
          open={isConfirmationDialogOpen}
          positiveAction={handleDeleteStepConfirm}
          negativeAction={() => setIsConfirmationDialogOpen(false)}
        />
        <Menu
          anchorEl={menuButtonRef.current}
          open={isMenuOpen}
          onClose={handleMenuClose}>
          <MenuItem onClick={handleDeleteStepClick}>
            {t('workflows.deleteStep')}
          </MenuItem>
        </Menu>
      </StyledBox>
    );
  },
);

export default SimplifiedWorkflowStep;
